import { ContentArea, MainContent } from '../styles'
import SectionContainer from './SectionContainer/SectionContainer'
import { AssignmentBodyItem, AssignmentPage } from 'interfaces/assignments'
import QuestionSetContainer from './QuestionSetContainer/QuestionSetContainer'
import { ViewMode } from 'interfaces/app'
import { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { SelectAssignmentState } from 'app/reducer/assignmentSlice'
import { isQuestionSetPage, isSectionPage } from '../utils'
import { QuestionnaireContext } from 'components/Assessment/QuestionnaireContext'
import Highlighter from 'assets/img/highlighter.svg'
import Eraser from 'assets/img/eraser.svg'

const SectionRoot = styled.div`
  position: relative;
  width: 100%;
  background: ${(props) => props.theme.contrast.background.primary};
  height: 100%;
`

type MainAssignmentSectionProps = {
  currentPage: AssignmentPage
  viewMode: ViewMode
  onAnswerChange: (questionId: number, selectedChoice: number) => void
}

/**
 * Renders a single assignment page section and all authoring controls that belong to it.
 *
 * Core responsibilities in this component:
 * 1) Derive page mode and page type flags (section, question set, teacher guide).
 * 2) Resolve relationship state (reference section, linked previous question set, linked section).
 * 3) Expose author actions (link, unlink, create stimulus, open title edit modals).
 * 4) Build the body-item list according to viewing restrictions (student/teacher/edit modes).
 * 5) Render the correct content container for the current page type.
 *
 * This keeps page-level orchestration in one place while delegating content rendering to
 * `SectionContainer`, `QuestionSetContainer`, and `TeacherGuideContainer`.
 */
const MainAssignmentSection = ({
  currentPage,
  viewMode,
  onAnswerChange
}: MainAssignmentSectionProps) => {
  const isStudentMode = viewMode === ViewMode.Student
  const isTeacherMode = false
  const readOnly = isStudentMode || isTeacherMode
  const isCurrentSectionPage = isSectionPage(currentPage)
  const isCurrentQuestionSetPage = isQuestionSetPage(currentPage)
  const { isHighlighterActive, showHighlightEraser, isPausedByTeacher } =
    useContext(QuestionnaireContext)
  const { body_items = [] } = currentPage || {}
  const { assignment } = useSelector(SelectAssignmentState)
  /**
   * A question set is able to reference a section page. the reference is however different from a link
   * A reference is to tell the students about a previous section they need to refer to as part of answering questions
   */
  const referencedSection = assignment?.pages?.find(
    (page: AssignmentPage) =>
      currentPage.reference_section_id &&
      currentPage?.reference_section_id === page.id
  )

  /**
   * This determines what should be considered as the root stimulus owner
   * for a linked question-set chain, the following logic applies:
   * 1. teacher guide
   * 2. section
   * 3. question set page (owns stimulus for page 4,5)
   * 4. question set page (linked_previous_qs_id -> page 3)
   * 5. question set page (linked_previous_qs_id -> page 4)
   * 6. section
   * 7. question set page (owns stimulus for page 8,9)
   * 8. question set page (linked_previous_qs_id -> page 7)
   * 9. question set page (linked_previous_qs_id -> page 8)
   *
   * In the above example:
   * - page 3 is stimulus source for pages 3,4,5
   * - page 7 is stimulus source for pages 7,8,9
   *
   * Logic flow:
   * 1) Build a page-id lookup map for O(1) chain hops.
   * 2) Start traversal at currentPage.
   * 3) Follow linked_previous_qs_id until there is no previous link.
   * 4) Stop early for malformed data (missing linked page, wrong page type, or circular links).
   * 5) Return the last valid page reached as the stimulus source.
   */
  const stimulusSourcePage = useMemo(() => {
    if (
      !isCurrentQuestionSetPage ||
      !currentPage ||
      !assignment?.pages?.length
    ) {
      return currentPage || null
    }

    const pagesById = new Map<number, AssignmentPage>(
      assignment.pages.map((page: AssignmentPage) => [page.id, page])
    )

    let sourcePage: AssignmentPage | null = currentPage
    const visitedPageIds = new Set<number>()

    /**
     * Walk backward through the linked question-set chain.
     */
    while (sourcePage) {
      const nextLinkedPageId = sourcePage.linked_previous_qs_id

      /** No previous link means we reached the root owner of stimulus. */
      if (!nextLinkedPageId) {
        break
      }

      /** Protect against infinite loops caused by circular linking. */
      if (visitedPageIds.has(sourcePage.id)) {
        break
      }

      visitedPageIds.add(sourcePage.id)

      const previousLinkedPage = pagesById.get(nextLinkedPageId)

      /**
       * Stop when the linked target is invalid for question-set stimulus sharing.
       */
      if (
        !previousLinkedPage ||
        previousLinkedPage.page_type !== 'question_set'
      ) {
        break
      }

      sourcePage = previousLinkedPage
    }

    return sourcePage || currentPage
  }, [assignment?.pages, currentPage, isCurrentQuestionSetPage])

  const hasStimulusContent = Boolean(
    (stimulusSourcePage as any)?.stimulus_tab_one?.trim() ||
    (stimulusSourcePage as any)?.stimulus_tab_two?.trim()
  )

  /**
   * Builds the renderable body items list for the active view mode.
   *
   * Mode-specific visibility rules:
   * 1) Student mode hides `teacher_block` items.
   *
   * The final list is always sorted by `order_index` to preserve author-defined sequence.
   */
  const renderedBodyItems = useMemo(() => {
    /**
     * Returns a new array sorted by `order_index`.
     *
     * We clone before sorting to avoid mutating source state arrays.
     */
    const sortByOrderIndex = (items: AssignmentBodyItem[]) => {
      return [...items].sort((a, b) => a.order_index - b.order_index)
    }

    if (isStudentMode) {
      return sortByOrderIndex(
        body_items.filter((item) => item.body_item_type !== 'teacher_block')
      )
    }

    return sortByOrderIndex(body_items)
  }, [isStudentMode, body_items])
  return (
    <MainContent
      id='highlight'
      style={{
        cursor: isHighlighterActive
          ? `url(${Highlighter}) 0 35, move`
          : showHighlightEraser
            ? `url(${Eraser}) 14 41, move`
            : ''
      }}
    >
      <SectionRoot className='flex-col' id='sketchContainerELA'>
        {isPausedByTeacher && (
          <div>
            <div className='mainContent-backDrop'></div>
            <div className='mainContent-alert loading'>
              <p>
                Assignment has been paused by instructor <span>.</span>
                <span>.</span>
                <span>.</span>
              </p>
            </div>
          </div>
        )}
        <ContentArea $width={hasStimulusContent ? '100%' : '1200px'}>
          {isCurrentSectionPage && (
            <SectionContainer
              readOnly={readOnly}
              bodyItems={renderedBodyItems}
            />
          )}
          {isCurrentQuestionSetPage && (
            <QuestionSetContainer
              readOnly={readOnly}
              referencedSection={referencedSection}
              bodyItems={renderedBodyItems}
              stimulusSourcePage={stimulusSourcePage}
              onAnswerChange={onAnswerChange}
            />
          )}
        </ContentArea>
      </SectionRoot>
    </MainContent>
  )
}

export default MainAssignmentSection
